import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { HideAll, ShowAll } from '@sas/react-svg';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useRefArray } from '../../hooks/useRefArray';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { ButtonVariant } from '../Button';
import { InvisibleText } from '../InvisibleText/InvisibleText';
import { GlobalSettingsKeys } from '../Settings';
import { useGetGlobalSetting } from '../Settings/GlobalSettingsProvider';
import { Toolbar } from '../Toolbar';
import { DraggableTreeNode } from './dnd/DraggableTreeNode';
import { TreeNodeDragPreview } from './dnd/TreeNodeDragPreview';
import { useTreeNavigation } from './hooks/useTreeNavigation';
import { TreeNodeNavigationKeys, useTreeNodeKeyboardHandler } from './hooks/useTreeNodeKeyboardHandler';
import { TreeNode } from './TreeNode';
import bundle from './NovaComponents-Tree-gui-icu.properties';
import cn from './Tree.css.json';
import './Tree.css';
import { useIsRTL } from '../../locale';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const generateTreeDescriptionId = createUseHtmlId('tree-description-header');
export function byId(nodes, id) {
  function get(nodes, id) {
    if (!nodes) return null;
    const potentialMatch = nodes.find(n => n.id === id);
    if (potentialMatch) return potentialMatch;
    for (const node of nodes) {
      const nextNodes = get(node.children, id);
      if (nextNodes) return nextNodes;
    }
    return null;
  }
  return get(nodes, id);
}
export const Tree = /*#__PURE__*/forwardRef((props, ref) => {
  const localize = useTextLocalizer();
  const useTreeDndPrefixId = createUseHtmlId('Tree-dndPrefix');
  const defaultDndPrefix = useTreeDndPrefixId();
  const dndPrefix = props.dndPrefix ?? defaultDndPrefix;
  const dragType = props.dragType ?? 'treeNode';
  const isRTL = useIsRTL();
  const selectionMode = props.selectionMode ?? 'single';
  const hideAnimation = props.hideAnimation ?? false;
  const shouldPlayAnimation = useGetGlobalSetting(GlobalSettingsKeys.PLAY_VISUAL_EFFECTS) === 'true';
  const allowDrag = props.allowDrag ?? function (_) {
    return true;
  };
  const [isDragging, setDragging] = useState(false);
  const draggableSelectedNodeCount = useRef(0);
  const flatData = props.nodes;

  // Most components like Settings need this feature only for initial rendering hence being worked out
  function getSeedFocusIndex() {
    const selectedNodes = props.selectedNodes ?? {};
    const selectedNodeKeys = Object.keys(selectedNodes);
    const sortedSelectedNodeIndices = selectedNodeKeys.map(selectedNodeValue => flatData.findIndex(flatdataValue => flatdataValue.node.id === selectedNodeValue)).sort();
    return sortedSelectedNodeIndices.length > 0 ? sortedSelectedNodeIndices[0] > -1 ? sortedSelectedNodeIndices[0] : 0 : 0;
  }
  // But few components like ContentSelector had to render the selected nodes after intialization.
  const lazyLoadingRef = useRef(flatData.length === 0);
  const [treeNodesWidth, setTreeNodesWidth] = useState();
  const [seedFocusindex] = useState(() => getSeedFocusIndex());
  const treeContainerRef = useRef(null);

  // current focus index is maintained as a Ref and not as a state since
  // we do not want un-necessary re-render while navigating visible nodes.
  const currentFocusedIndexRef = useRef(seedFocusindex);
  const getCurrentFocusIndex = () => currentFocusedIndexRef.current;
  const setCurrentFocusIndex = value => currentFocusedIndexRef.current = value;

  // for scroll induced by keyboard navigator actions
  const isProgrammaticScrolledRef = useRef(false);

  // for specific use case of performing keyboard action on a node in focus but out of view.
  const postFocusKeyboardActionRef = useRef();

  // need a ref to clear tabindex
  const focusedNode = useRef();
  const {
    refs,
    createSetRef
  } = useRefArray();
  const isMouseDownRef = useRef(false);
  const updateNodeWidth = useCallback(() => {
    const treeNodeContainerWidth = treeContainerRef?.current?.clientWidth;
    let maxNodeWidth = treeNodeContainerWidth ?? 0;
    const nodeRefs = [...refs];
    for (const item of nodeRefs) {
      if (item && treeNodeContainerWidth) {
        maxNodeWidth = maxNodeWidth < item?.scrollWidth ? item?.scrollWidth : maxNodeWidth;
      }
    }
    setTreeNodesWidth(maxNodeWidth);
  }, [refs]);
  const [treeWidth, setTreeWidth] = useState(0);
  useEffect(() => {
    const seedFocusIndex = getSeedFocusIndex();
    if (lazyLoadingRef.current && seedFocusIndex) {
      setCurrentFocusIndex(seedFocusIndex);
      // change the focused index when there was no nodes initially and the selection is not the first node
      lazyLoadingRef.current = false;
    }
    if (props.enableHorizontalScroll) {
      setTreeWidth(treeContainerRef.current?.clientWidth);
      updateNodeWidth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatData, props.selectedNodes, props.expandedNodes, updateNodeWidth, props.enableHorizontalScroll, treeNodesWidth, treeWidth]); // update the seed focus index when the flat nodes or selected node are changed.

  useEffect(() => {
    // added for content selector NOVACOMMONS-18735
    const seedFocusIndex = getSeedFocusIndex();
    if (Object.keys(props.selectedNodes ?? {}).length === 1 && selectionMode === 'single') {
      setCurrentFocusIndex(seedFocusIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(props.selectedNodes ?? {})[0]]);

  // detect scroll awareness
  const [scrollTop, setScrollTop] = useState(0);
  const onScroll = useCallback(() => {
    const treeContainerDom = treeContainerRef?.current;
    if (treeContainerDom) {
      setScrollTop(treeContainerDom.scrollTop);
      if (!isDragging && isMouseDownRef.current) {
        focusedNode.current?.setAttribute('tabindex', '-1');
        treeContainerDom.setAttribute('tabindex', '0');
        treeContainerDom.focus();
      }
    }
  }, [isDragging]);
  useEffect(() => {
    const treeContainerDom = treeContainerRef?.current;
    if (treeContainerDom) {
      treeContainerDom.addEventListener('scroll', onScroll);
      return () => {
        treeContainerDom.removeEventListener('scroll', onScroll);
      };
    }
  }, [onScroll]);

  // defaults only come into play during tests execution.
  const [treeHeight, setTreeHeight] = useState(500);
  const [nodeHeight, setNodeHeight] = useState(24);
  const _updateHeight = useCallback(() => {
    if (!isDragging) {
      const treeDom = treeContainerRef.current;
      const nodeDom = treeDom?.querySelector('li');
      if (treeDom && treeDom.getBoundingClientRect().height > 0) {
        setTreeHeight(treeDom.getBoundingClientRect().height);
      }
      if (nodeDom && nodeDom.getBoundingClientRect().height > 0) {
        setNodeHeight(nodeDom.getBoundingClientRect().height);
      }
    }
  }, [isDragging]);

  // to prevent vertical scrollbar flicker when the available height is less than the default height.
  useLayoutEffect(() => {
    _updateHeight();
  }, [_updateHeight]);
  const [setTreeReSizeRef] = useResizeObserverRef(() => {
    _updateHeight();
    if (props.enableHorizontalScroll && treeWidth !== treeContainerRef.current?.clientWidth) {
      setTreeWidth(treeContainerRef.current?.clientWidth);
      setTreeNodesWidth(value => value === 0 ? undefined : 0);
    }
  });

  // page calculation
  const bufferNodes = 5;
  const nodeCount = flatData.length;
  const totalHeight = Math.max(nodeCount * nodeHeight, treeHeight);
  const maxScrollTop = Math.max(0, Math.round(totalHeight - treeHeight));

  // first rendered node index in the displayed page
  const startNodeIndex = Math.max(0, Math.floor(Math.min(scrollTop, maxScrollTop) / nodeHeight) - bufferNodes);

  // total rendered row count in the displayed page
  const renderedNodeCount = Math.min(nodeCount - startNodeIndex, Math.ceil(treeHeight / nodeHeight) + 2 * bufferNodes);
  if (getCurrentFocusIndex() > nodeCount - 1) {
    setCurrentFocusIndex(0);
  }
  const focusTreeNode = index => {
    refs[index]?.focus();
    if (postFocusKeyboardActionRef.current) {
      const keyboardAction = postFocusKeyboardActionRef.current;
      postFocusKeyboardActionRef.current = undefined;
      keyboardAction && onKeyboardAction(getCurrentFocusIndex(), keyboardAction);
    }
    if (props.onFocusChange) {
      props.onFocusChange(flatData[getCurrentFocusIndex()]?.node);
    }
  };
  const setProgrammaticScrollTop = value => {
    isProgrammaticScrolledRef.current = true;
    const treeContainerDom = treeContainerRef.current;
    if (treeContainerDom) {
      treeContainerDom.scrollTop = value;
    }
  };
  const navigator = useTreeNavigation({
    treeHeight,
    nodeHeight,
    nodeCount
  }, {
    scrollTop,
    setScrollTop: setProgrammaticScrollTop,
    startNodeIndex,
    renderedNodeCount
  }, {
    getCurrentFocusIndex,
    setCurrentFocusIndex,
    focusTreeNode
  });
  useImperativeHandle(props.treeRef, () => ({
    focus() {
      treeContainerRef.current?.focus();
    },
    scrollToNode(index) {
      navigator.scrollToIndex(index);
    },
    getScrollTop() {
      return treeContainerRef.current?.scrollTop ?? 0;
    },
    setScrollTop(value) {
      setProgrammaticScrollTop(value);
    }
  }), [navigator]);
  const onKeyboardAction = useTreeNodeKeyboardHandler(flatData, props, navigator, {
    treeContainerRef
  });

  // the re-render is triggered on both interactive or programmatic scrolling.
  useEffect(() => {
    const indexToFocus = getCurrentFocusIndex() - startNodeIndex;
    if (isProgrammaticScrolledRef.current && refs[indexToFocus]) {
      isProgrammaticScrolledRef.current = false;
      focusTreeNode(indexToFocus);
    }
  });
  const onDeleteKeyPress = index => {
    if (index === 0) return;
    const currentFocusedIndex = getCurrentFocusIndex();
    if (currentFocusedIndex === nodeCount - 1) {
      setCurrentFocusIndex(currentFocusedIndex - 1);
    }
    let currentRow = 0;
    if (startNodeIndex !== 0) {
      // in case of virtualized tree, startNodeIndex gets updated (increased by 1) after deleting a node
      // so use the old value of getCurrentFocusIndex()
      currentRow = currentFocusedIndex - startNodeIndex;
    } else {
      // when node is deleted from the first page of the tree or the non-virtualized tree where all nodes are visible
      currentRow = getCurrentFocusIndex() - startNodeIndex;
    }

    // set the focus to the next node after the nodes state has been updated by the consumer
    setTimeout(() => {
      if (!refs[currentRow]) {
        focusTreeNode(currentRow - 1);
      } else {
        focusTreeNode(currentRow);
      }
    }, 0);
  };
  function onArrowClick(id) {
    const targetNode = flatData.find(item => item.node.id === id)?.node;
    if (targetNode) {
      const isExpanded = props.expandedNodes[id] !== true; // Flip the expanded state of this node
      if (props.enableHorizontalScroll) {
        setTreeNodesWidth(0);
      }
      props.onExpand(targetNode, isExpanded);
    }
  }
  function scrollFocusNodeNodeIntoViewIfClipped() {
    if (treeContainerRef.current && focusedNode.current) {
      const treeContainerDom = treeContainerRef.current;
      const focusedNodeDom = focusedNode.current;
      if (focusedNodeDom.getBoundingClientRect().bottom > treeContainerDom.getBoundingClientRect().bottom) {
        setCurrentFocusIndex(getCurrentFocusIndex() - 1);
        navigator.down();
      } else if (focusedNodeDom.getBoundingClientRect().top < treeContainerDom.getBoundingClientRect().top) {
        setCurrentFocusIndex(getCurrentFocusIndex() + 1);
        navigator.up();
      }
    }
  }
  function getTop(rowIndex) {
    let top = 0;
    if (totalHeight > treeHeight) {
      top = startNodeIndex * nodeHeight + nodeHeight * rowIndex;
    } else {
      top = nodeHeight * rowIndex;
    }
    return top;
  }
  const dragStart = draggedItem => {
    props.onDragStart?.(draggedItem);
    setDragging(true);
  };
  const dragEnd = (draggedItem, monitor) => {
    setDragging(false);
    props.onDragEnd?.(draggedItem, monitor);
  };
  const createTreeNodeWithDnd = (flatNode, nodeIndex, rowIndex) => {
    return /*#__PURE__*/_jsx(DraggableTreeNode, {
      dndPrefix: dndPrefix,
      dragType: dragType,
      allowDrag: props.allowDrag?.(flatNode.node),
      id: flatNode.node.id,
      node: flatNode.node,
      onDragStart: dragStart,
      onDragEnd: dragEnd,
      dragOptions: props.dragOptions,
      children: createTreeNode(flatNode, nodeIndex, rowIndex)
    }, flatNode.node.id);
  };
  const scrollFocusedNodeIntoViewHorizontally = () => {
    const treeContainer = treeContainerRef.current;
    const focusedTreeNode = focusedNode.current;
    if (treeContainer && focusedTreeNode && props.enableHorizontalScroll && !isMouseDownRef.current && (isRTL ? treeContainer.scrollLeft < 0 : treeContainer.scrollLeft > 0)) {
      let shouldScrollToStart = false;
      if (isRTL) {
        const treeContainerInlineStart = treeContainer.getBoundingClientRect().right ?? 0;
        const focusedTreeNodeInlineStart = focusedTreeNode.getBoundingClientRect().right ?? 0;
        shouldScrollToStart = focusedTreeNodeInlineStart > treeContainerInlineStart;
      } else {
        const treeContainerInlineStart = treeContainer.getBoundingClientRect().left ?? 0;
        const focusedTreeNodeInlineStart = focusedTreeNode.getBoundingClientRect().left ?? 0;
        shouldScrollToStart = focusedTreeNodeInlineStart < treeContainerInlineStart;
      }
      if (shouldScrollToStart) treeContainer.scrollLeft = 0;
    }
  };
  const createTreeNode = (flatNode, nodeIndex, rowIndex) => {
    const {
      node,
      depth,
      parentId,
      siblingCount,
      siblingPosition
    } = flatNode;
    const isExpanded = props.expandedNodes[node.id] === true;
    const treeNodeProps = {
      depth,
      siblingCount,
      siblingPosition,
      node,
      hideAnimation: !shouldPlayAnimation || hideAnimation,
      testId: node.testId ?? postfixTestId(props.testId, node.id),
      expanded: isExpanded,
      enableHorizontalScroll: props.enableHorizontalScroll,
      onArrowClick,
      isChildNode: !!parentId,
      hasChildren: Array.isArray(node.children) && node.children.length > 0,
      onDoubleClick: targetNode => {
        if (!targetNode.disabled) {
          if (targetNode.isActionable) {
            props.onActivate?.(targetNode);
          }
          props.onDoubleClick?.(targetNode);
        }
      },
      onSelect: (targetNode, eventData) => {
        if (props.onSelect) {
          if (targetNode) props.onSelect(targetNode, eventData);
        }
        scrollFocusNodeNodeIntoViewIfClipped();
      },
      tabIndex: getCurrentFocusIndex() === nodeIndex ? 0 : -1,
      onFocus: event => {
        setCurrentFocusIndex(nodeIndex);
        focusedNode.current?.setAttribute('tabindex', '-1');
        event.target.setAttribute('tabindex', '0');
        focusedNode.current = refs[refs.indexOf(event.currentTarget)];
        scrollFocusedNodeIntoViewHorizontally();
      },
      onContext: !node.disabled ? props.onContextMenu : undefined,
      onKeyUp: event => {
        if ((selectionMode === 'desktop' || selectionMode === 'parentChild') && event.shiftKey && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
          const {
            key: keyPressed,
            ctrlKey,
            shiftKey,
            metaKey
          } = event;

          // needed for virtualization case when keydown is continuously pressed
          setTimeout(function (data) {
            let nodeIndex = getCurrentFocusIndex();
            if (event.key === 'ArrowDown') {
              nodeIndex--;
            } else {
              nodeIndex++;
            }
            if (nodeIndex < 0) nodeIndex = 0;
            if (nodeIndex >= props.nodes.length) nodeIndex = props.nodes.length - 1;
            props.onSelect(props.nodes[nodeIndex].node, data);
          }.bind(null, {
            keyPressed,
            ctrlKey,
            shiftKey,
            metaKey
          }), 0);
        }
      },
      onKeyDown: event => {
        if (event.key === 'Delete' && node.isDeletable) {
          onDeleteKeyPress(nodeIndex);
        }
        onKeyboardAction(nodeIndex, {
          keyPressed: event.key,
          ctrlKey: event.ctrlKey,
          shiftKey: event.shiftKey,
          metaKey: event.metaKey
        });
        if (Object.values(TreeNodeNavigationKeys).includes(event.key)) {
          event.preventDefault();
        }
        if ((selectionMode === 'desktop' || selectionMode === 'parentChild') && event.shiftKey && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
          // If I use state to manage this, it displays big lag in virtualization scenario.
          const {
            key: keyPressed,
            ctrlKey,
            shiftKey,
            metaKey
          } = event;
          setTimeout(function (node, data) {
            props.onSelect(node, data);
          }.bind(null, node, {
            keyPressed,
            ctrlKey,
            shiftKey,
            metaKey
          }), 0);
        }
      },
      getSelectionMode: () => selectionMode,
      disableClickDelay: props.disableClickDelay
    };
    if (selectionMode === 'single') {
      props.selectedNodes;
    }
    const treeNodeAnimation = !shouldPlayAnimation || hideAnimation ? undefined : {
      initial: {
        transition: {
          top: {
            duration: 0
          }
        }
      },
      closed: {
        transition: {
          top: {
            duration: 0.3,
            ease: [0, 0.5, 0.2, 1]
          }
        }
      },
      open: {
        top: `${getTop(rowIndex)}px`,
        transition: {
          top: {
            duration: 0.3,
            ease: [0, 0.5, 0.2, 1]
          }
        }
      }
    };
    return /*#__PURE__*/_jsx(motion.li, {
      variants: treeNodeAnimation,
      initial: "initial",
      animate: "open",
      exit: "closed",
      role: "presentation",
      style: {
        position: 'absolute',
        top: `${getTop(rowIndex)}px`,
        width: '100%'
      },
      children: selectionMode === 'parentChild' ? /*#__PURE__*/_jsx(TreeNode, {
        checked: (props.selectedNodes && props.selectedNodes[node.id]) ?? false,
        ...treeNodeProps,
        ref: createSetRef(rowIndex)
      }) : /*#__PURE__*/_jsx(TreeNode, {
        selected: (props.selectedNodes && !!props.selectedNodes[node.id]) ?? false,
        ...treeNodeProps,
        ref: createSetRef(rowIndex)
      })
    }, node.id);
  };
  const dt = useDesignTokens();
  const tokens = renderInlineCssVars(getTreeTokens(dt), 'tree');
  const items = [];
  const titlelblId = props.id ? `${props.id}-title-lbl` : 'title-lbl';
  const isTitleExist = props.title && props.title.trim().length > 0;
  isTitleExist && items.push({
    id: titlelblId,
    type: 'Heading',
    testId: postfixTestId(props.testId, 'titleLbl'),
    level: 4,
    className: cn.title,
    children: props.title
  });
  (isTitleExist || props.showExpandCollapse === true) && items.push({
    type: 'ToolbarSpacer'
  });
  props.showExpandCollapse === true && items.push({
    type: 'Button',
    variant: ButtonVariant.Icon,
    icon: {
      icon: HideAll
    },
    testId: postfixTestId(props.testId, 'collapseAllBtn'),
    ariaLabel: localize('hideall.button.aria'),
    onPress: () => {
      setCurrentFocusIndex(0);
      startNodeIndex === 0 && refs[0]?.setAttribute('tabindex', '0');
      props.onCollapseAll && props.onCollapseAll();
      props.enableHorizontalScroll && setTreeNodesWidth(0);
    }
  }, {
    type: 'Button',
    variant: ButtonVariant.Icon,
    icon: {
      icon: ShowAll
    },
    testId: postfixTestId(props.testId, 'expandAllBtn'),
    ariaLabel: localize('showall.button.aria'),
    onPress: () => {
      setCurrentFocusIndex(0);
      startNodeIndex === 0 && refs[0]?.setAttribute('tabindex', '0');
      props.onExpandAll && props.onExpandAll();
      props.enableHorizontalScroll && setTreeNodesWidth(0);
    }
  });
  const selectedNodeKeys = Object.keys(props.selectedNodes ?? {});
  draggableSelectedNodeCount.current = selectedNodeKeys.length > 0 ? flatData.filter(flatNode => selectedNodeKeys.indexOf(flatNode.node.id) > -1 && allowDrag(flatNode.node)).length : 0;
  const getTreeNodesContainer = () => /*#__PURE__*/_jsxs("div", {
    className: cn.nodesContainer,
    style: {
      overflow: 'auto'
    },
    role: props.role,
    "data-landmark-label": props.landmarkLabel,
    "aria-label": props.nodesContainerAriaLabel,
    ref: ele => {
      treeContainerRef.current = ele;
      setTreeReSizeRef(ele);
      setRef(ref, ele);
    },
    tabIndex: 0,
    onMouseDown: () => isMouseDownRef.current = true,
    onMouseUp: () => isMouseDownRef.current = false,
    onFocus: e => {
      treeContainerRef.current?.setAttribute('tabindex', '-1');
      if (e.target === e.currentTarget && isMouseDownRef.current === false) {
        navigator.scrollToFocusedNode();
        if (lazyLoadingRef.current && flatData.length > 0) lazyLoadingRef.current = false;
        // If the tree is focused with loaded nodes, set the flag to false in case no lazy selection node id
      }
    },

    onKeyDown: event => {
      if (event.target === event.currentTarget && Object.values(TreeNodeNavigationKeys).includes(event.key)) {
        const eventData = {
          keyPressed: event.key,
          ctrlKey: event.ctrlKey,
          shiftKey: event.shiftKey,
          metaKey: event.metaKey
        };
        focusedNode.current?.setAttribute('tabindex', '-1');
        treeContainerRef.current?.setAttribute('tabindex', '-1');
        postFocusKeyboardActionRef.current = eventData;
        navigator.scrollToFocusedNode();
        event.preventDefault();
      }
    },
    children: [/*#__PURE__*/_jsx(AnimatePresence, {
      initial: false,
      children: /*#__PURE__*/_jsx("ul", {
        onContextMenu: e => {
          if (e.currentTarget === e.target) e.stopPropagation();
          e.preventDefault();
        },
        role: "tree",
        className: cn.nodes,
        style: {
          position: 'relative',
          ...(props.enableHorizontalScroll ? {
            height: totalHeight - 16,
            width: treeNodesWidth === 0 ? '100%' : `${treeNodesWidth}px`
          } : {
            overflow: 'hidden',
            height: totalHeight
          })
        },
        ...(ariaLabelledByAttr && {
          'aria-labelledby': ariaLabelledByAttr
        }),
        "aria-label": props.ariaLabel,
        ...(ariaDescribedByAttr && {
          'aria-describedby': ariaDescribedByAttr
        }),
        ...((selectionMode === 'desktop' || selectionMode === 'parentChild') && {
          'aria-multiselectable': true
        }),
        children: new Array(renderedNodeCount).fill(null).map((_, index) => flatData[index + startNodeIndex] ? props.allowDrag?.(flatData[index + startNodeIndex].node) ? createTreeNodeWithDnd(flatData[index + startNodeIndex], index + startNodeIndex, index) : createTreeNode(flatData[index + startNodeIndex], index + startNodeIndex, index) : null)
      })
    }), props.allowDrag ? /*#__PURE__*/_jsx(TreeNodeDragPreview, {
      dndPrefix: dndPrefix,
      dragType: dragType,
      draggedNodeCount: draggableSelectedNodeCount.current
    }) : null]
  });
  const defaultAriaDescribedBy = useMemo(() => {
    let treeNodehasActionableProperty = false,
      treeNodehasDeletableProperty = false;
    for (const item of props.nodes) {
      if (item.node.isActionable) {
        treeNodehasActionableProperty = true;
      }
      if (item.node.isDeletable) {
        treeNodehasDeletableProperty = true;
      }
      if (treeNodehasActionableProperty && treeNodehasDeletableProperty) {
        break;
      }
    }
    if (treeNodehasActionableProperty && treeNodehasDeletableProperty) {
      return localize('tree.activateAndDeleteKeys.msg');
    } else if (treeNodehasActionableProperty) {
      return localize('tree.activateUsingEnterKey.msg');
    } else if (treeNodehasDeletableProperty) {
      return localize('tree.deleteUsingDeleteKey.msg');
    }
    return '';
  }, [localize, props.nodes]);
  const treeAriaDescribedBy = generateTreeDescriptionId();
  let ariaLabelledByAttr = '';
  if (isTitleExist) {
    ariaLabelledByAttr = titlelblId;
  }
  if (props.ariaLabelledBy) {
    ariaLabelledByAttr = ariaLabelledByAttr.concat(` ${props.ariaLabelledBy}`).trimLeft();
  }
  let ariaDescribedByAttr = '';
  if (props.ariaDescribedBy) {
    ariaDescribedByAttr = props.ariaDescribedBy;
  }
  // related to TreeNode actions
  if (defaultAriaDescribedBy) {
    ariaDescribedByAttr = ariaDescribedByAttr.concat(` ${treeAriaDescribedBy}`).trimLeft();
  }
  return /*#__PURE__*/_jsxs("div", {
    className: cx(cn.container, props.className),
    style: {
      ...tokens,
      ...props.style
    },
    "data-testid": props.testId,
    onBlur: event => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        treeContainerRef.current?.setAttribute('tabindex', '0');
      }
    },
    children: [defaultAriaDescribedBy !== '' ? /*#__PURE__*/_jsx(InvisibleText, {
      testId: "tree-description",
      id: treeAriaDescribedBy,
      children: defaultAriaDescribedBy
    }) : null, props.title && props.title.trim().length >= 0 || props.showExpandCollapse === true ? /*#__PURE__*/_jsx(Toolbar, {
      items: items
    }) : null, getTreeNodesContainer()]
  });
});
export const getTreeTokens = dt => {
  /* High Contrast uses an inversion filter 'invert(1) grayscale(1)' to apply the selected state so that
  any components inside a TreeNode will remain accessible when the Node is selected. */
  return {
    fg: dt.tokens['color-action-text-subtle-normal'],
    'selected-bg': dt.themeVariant === 'high-contrast' ? dt.tokens['color-action-background-control-normal'] : dt.tokens['color-action-background-subtle-selected'],
    'selected-inactive-bg': dt.themeVariant === 'high-contrast' ? dt.tokens['color-action-background-control-normal'] : dt.tokens['color-action-background-subtle-selected-inactive'],
    'selected-fg': dt.themeVariant === 'high-contrast' ? dt.tokens['color-action-text-control-normal'] : dt.tokens['color-action-text-subtle-selected'],
    'selected-filter': dt.themeVariant === 'high-contrast' ? 'invert(1) grayscale(1)' : 'none',
    /* Using hard code value for  High Contrast uses (as token not available in VD) an inversion filter 'invert(0.85) grayscale(1)' to apply the selected-inactive state so that
    any components inside a TreeNode will remain accessible when the Node is selected. */
    'selected-inactive-filter': dt.themeVariant === 'high-contrast' ? 'invert(0.85) grayscale(1)' : 'none',
    'disabled-fg': dt.tokens['color-action-text-standard-disabled'],
    'spacing-xxs': dt.tokens['space-base-xxs'],
    'spacing-xs': dt.tokens['space-base-xs'],
    'spacing-s': dt.tokens['space-base-s'],
    'font-size-m': dt.tokens['font-size-body-text-m'],
    'font-weight-thick': dt.tokens['font-style-weight-700']
  };
};
//# sourceMappingURL=Tree.js.map